import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  listRubrics,
  deleteRubric,
  RubricSummary,
} from "../services/rubricAdminService";
import ConfirmModal from "./admin/ConfirmModal";

interface RubricsTabProps {
  getToken: () => string | undefined;
}

const RubricsTab: React.FC<RubricsTabProps> = ({ getToken }) => {
  const navigate = useNavigate();
  const { orgSlug } = useParams();
  const [rubrics, setRubrics] = useState<RubricSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RubricSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRubrics(await listRubrics(getToken()));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load rubrics");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteRubric(pendingDelete.id, getToken());
      setPendingDelete(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
      setPendingDelete(null);
    }
  };

  return (
    <div className="text-on-surface">
      <div className="flex items-center justify-end mb-4">
        <button
          type="button"
          className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold"
          onClick={() => navigate(`/${orgSlug}/rubrics/new`)}
        >
          + New Rubric
        </button>
      </div>

      {error && <div className="mb-3 text-error text-sm">{error}</div>}
      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : rubrics.length === 0 ? (
        <div className="text-slate-400 text-sm">No rubrics yet. Create one.</div>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-slate-400 border-b border-outline-variant/30">
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">Framework</th>
              <th className="py-2 pr-4">Criteria</th>
              <th className="py-2 pr-4">Scenarios</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {rubrics.map((r) => (
              <tr key={r.id} className="border-b border-outline-variant/20 hover:bg-surface-container">
                <td className="py-2 pr-4 font-semibold">{r.name}</td>
                <td className="py-2 pr-4">{r.frameworkName}</td>
                <td className="py-2 pr-4">{r.criteriaCount}</td>
                <td className="py-2 pr-4">{r.allowedScenarioCount}</td>
                <td className="py-2 text-right whitespace-nowrap">
                  {r.canEdit ? (
                    <>
                      <button
                        type="button"
                        className="text-primary hover:underline mr-3"
                        onClick={() => navigate(`/${orgSlug}/rubrics/${r.id}/edit`)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-error hover:underline"
                        onClick={() => setPendingDelete(r)}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <span className="text-slate-500 text-xs">Global</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pendingDelete && (
        <ConfirmModal
          title="Delete rubric"
          body={`Delete "${pendingDelete.name}"? This cannot be undone.`}
          icon="delete"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          destructive
          onConfirm={confirmDelete}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

export default RubricsTab;
